import { useCallback, useEffect, useRef, useState } from 'react';
import useLocalStorage from './useLocalStorage';

interface UseResizableOptions {
  minWidth: number;
  maxWidth: number;
  defaultWidth: number;
  storageKey?: string;
  disabled?: boolean;
}

interface UseResizableReturn {
  width: number;
  isResizing: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
}

export function useResizable({
  minWidth,
  maxWidth,
  defaultWidth,
  storageKey,
  disabled = false
}: UseResizableOptions): UseResizableReturn {
  const [storedWidth, setStoredWidth] = useLocalStorage<number>(
    storageKey || 'resizable-width',
    defaultWidth
  );

  const [width, setWidth] = useState<number>(() => {
    if (storageKey) {
      return Math.min(Math.max(storedWidth, minWidth), maxWidth);
    }
    return defaultWidth;
  });

  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  const wasResizingRef = useRef(false);

  useEffect(() => {
    const targetWidth = storageKey ? storedWidth : width;
    const clampedWidth = Math.min(Math.max(targetWidth, minWidth), maxWidth);
    setWidth(clampedWidth);
  }, [minWidth, maxWidth, storedWidth, storageKey]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;

      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
    },
    [disabled, width]
  );

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e: MouseEvent): void => {
      const delta = startXRef.current - e.clientX;
      const newWidth = Math.min(
        Math.max(startWidthRef.current + delta, minWidth),
        maxWidth
      );
      setWidth(newWidth);
    };

    const handleMouseUp = (): void => {
      setIsResizing(false);
      if (storageKey) {
        setStoredWidth(width);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing, minWidth, maxWidth, storageKey, setStoredWidth, width]);

  useEffect(() => {
    if (wasResizingRef.current && !isResizing && storageKey) {
      setStoredWidth(width);
    }
    wasResizingRef.current = isResizing;
  }, [isResizing, width, storageKey, setStoredWidth]);

  return {
    width,
    isResizing,
    handleMouseDown
  };
}

export default useResizable;
